import { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, Clock3, ImagePlus, RefreshCcw, Save, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useClinicProfile } from '../../context/ClinicProfileContext';
import { ClientReportSettingsService } from '../../features/assessments-v2/services/ClientReportSettingsService';
import type { ClientReportSettings, ResolvedClientReportBranding } from '../../features/assessments-v2/types';
import { CLIENT_BRANDING_BUCKET, uploadClientBrandingImage } from '../../utils/storage';
import {
  BRAZIL_TIME_ZONE_OPTIONS,
  DEFAULT_TIME_ZONE,
  getTimeZoneLabel,
  isValidTimeZone,
  normalizeTimeZone,
} from '../../utils/timeZone';

type LogoField = 'logoPrint' | 'logoMark' | 'logoWordmark';

interface ClinicSettingsFormState {
  reportDisplayName: string;
  legalName: string;
  cnpj: string;
  reportSubtitle: string;
  logoPrintPath: string;
  logoPrintUrl: string;
  logoMarkPath: string;
  logoMarkUrl: string;
  logoWordmarkPath: string;
  logoWordmarkUrl: string;
  logoAlt: string;
  addressStreet: string;
  addressNumber: string;
  addressComplement: string;
  addressDistrict: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
  timeZone: string;
  contactPhone: string;
  contactPhoneFull: string;
  contactWhatsapp: string;
  contactWhatsappFull: string;
  contactEmail: string;
  footerLegalText: string;
}

const EMPTY_FORM: ClinicSettingsFormState = {
  reportDisplayName: '',
  legalName: '',
  cnpj: '',
  reportSubtitle: '',
  logoPrintPath: '',
  logoPrintUrl: '',
  logoMarkPath: '',
  logoMarkUrl: '',
  logoWordmarkPath: '',
  logoWordmarkUrl: '',
  logoAlt: '',
  addressStreet: '',
  addressNumber: '',
  addressComplement: '',
  addressDistrict: '',
  addressCity: '',
  addressState: '',
  addressPostalCode: '',
  timeZone: DEFAULT_TIME_ZONE,
  contactPhone: '',
  contactPhoneFull: '',
  contactWhatsapp: '',
  contactWhatsappFull: '',
  contactEmail: '',
  footerLegalText: '',
};

const EMPTY_SELECTED_FILES: Record<LogoField, File | null> = {
  logoPrint: null,
  logoMark: null,
  logoWordmark: null,
};

const LOGO_CONFIG = {
  logoPrint: {
    label: 'Logo para relatórios',
    description: 'Usada no cabeçalho principal dos laudos e PDFs.',
    pathField: 'logoPrintPath',
    urlField: 'logoPrintUrl',
    resolvedKey: 'logoPrintUrl',
    kind: 'logo-print',
  },
  logoMark: {
    label: 'Logo reduzida',
    description: 'Marca compacta para blocos menores e usos auxiliares.',
    pathField: 'logoMarkPath',
    urlField: 'logoMarkUrl',
    resolvedKey: 'logoMarkUrl',
    kind: 'logo-mark',
  },
  logoWordmark: {
    label: 'Logo horizontal',
    description: 'Versão wordmark para cabeçalhos e áreas institucionais.',
    pathField: 'logoWordmarkPath',
    urlField: 'logoWordmarkUrl',
    resolvedKey: 'logoWordmarkUrl',
    kind: 'logo-wordmark',
  },
} as const satisfies Record<
  LogoField,
  {
    label: string;
    description: string;
    pathField: keyof ClinicSettingsFormState;
    urlField: keyof ClinicSettingsFormState;
    resolvedKey: keyof ResolvedClientReportBranding;
    kind: 'logo-print' | 'logo-mark' | 'logo-wordmark';
  }
>;

const toFormState = (settings?: ClientReportSettings | null): ClinicSettingsFormState => ({
  reportDisplayName: settings?.reportDisplayName ?? '',
  legalName: settings?.legalName ?? '',
  cnpj: settings?.cnpj ?? '',
  reportSubtitle: settings?.reportSubtitle ?? '',
  logoPrintPath: settings?.logoPrintPath ?? '',
  logoPrintUrl: settings?.logoPrintUrl ?? '',
  logoMarkPath: settings?.logoMarkPath ?? '',
  logoMarkUrl: settings?.logoMarkUrl ?? '',
  logoWordmarkPath: settings?.logoWordmarkPath ?? '',
  logoWordmarkUrl: settings?.logoWordmarkUrl ?? '',
  logoAlt: settings?.logoAlt ?? '',
  addressStreet: settings?.addressStreet ?? '',
  addressNumber: settings?.addressNumber ?? '',
  addressComplement: settings?.addressComplement ?? '',
  addressDistrict: settings?.addressDistrict ?? '',
  addressCity: settings?.addressCity ?? '',
  addressState: settings?.addressState ?? '',
  addressPostalCode: settings?.addressPostalCode ?? '',
  timeZone: normalizeTimeZone(settings?.timeZone),
  contactPhone: settings?.contactPhone ?? '',
  contactPhoneFull: settings?.contactPhoneFull ?? '',
  contactWhatsapp: settings?.contactWhatsapp ?? '',
  contactWhatsappFull: settings?.contactWhatsappFull ?? '',
  contactEmail: settings?.contactEmail ?? '',
  footerLegalText: settings?.footerLegalText ?? '',
});

export default function ClinicSettingsPage() {
  const { selectedClient, user } = useAuth();
  const { refreshProfile } = useClinicProfile();
  const [formData, setFormData] = useState<ClinicSettingsFormState>(EMPTY_FORM);
  const [resolvedBranding, setResolvedBranding] = useState<ResolvedClientReportBranding | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Record<LogoField, File | null>>(EMPTY_SELECTED_FILES);
  const [logoPreviewUrls, setLogoPreviewUrls] = useState<Record<LogoField, string>>({
    logoPrint: '',
    logoMark: '',
    logoWordmark: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settingsLoadError, setSettingsLoadError] = useState<string | null>(null);
  const [loadedClientId, setLoadedClientId] = useState<string | null>(null);
  const settingsRequestIdRef = useRef(0);

  const clientId = selectedClient?.clientId ?? '';
  const activeClientIdRef = useRef(clientId);
  activeClientIdRef.current = clientId;

  const loadSettings = useCallback(async () => {
    const requestedClientId = clientId;
    const requestId = ++settingsRequestIdRef.current;
    const isCurrentRequest = () => (
      requestId === settingsRequestIdRef.current
      && activeClientIdRef.current === requestedClientId
    );
    setSettingsLoadError(null);
    setLoadedClientId(null);
    setFormData(EMPTY_FORM);
    setResolvedBranding(null);

    if (!requestedClientId) {
      setFormData(EMPTY_FORM);
      setResolvedBranding(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const [settings, branding] = await Promise.all([
        ClientReportSettingsService.getByClientId(requestedClientId),
        ClientReportSettingsService.resolveForClient(requestedClientId, selectedClient),
      ]);

      if (!isCurrentRequest()) return;

      setFormData(toFormState(settings));
      setResolvedBranding(branding);
      setSelectedFiles(EMPTY_SELECTED_FILES);
      setLoadedClientId(requestedClientId);
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error('Erro ao carregar dados da clínica:', error);
      setSettingsLoadError('Não foi possível carregar os dados da clínica com segurança.');
      toast.error('Não foi possível carregar os dados da clínica.');
    } finally {
      if (isCurrentRequest()) {
        setIsLoading(false);
      }
    }
  }, [clientId, selectedClient]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const urlsToRevoke: string[] = [];
    const nextPreviewUrls: Record<LogoField, string> = {
      logoPrint: '',
      logoMark: '',
      logoWordmark: '',
    };

    (Object.keys(LOGO_CONFIG) as LogoField[]).forEach((field) => {
      const file = selectedFiles[field];
      if (!file) {
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      nextPreviewUrls[field] = previewUrl;
      urlsToRevoke.push(previewUrl);
    });

    setLogoPreviewUrls(nextPreviewUrls);

    return () => {
      urlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedFiles]);

  const handleFieldChange = (field: keyof ClinicSettingsFormState, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelection = (field: LogoField, file?: File | null) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [field]: file ?? null,
    }));
  };

  const handleLogoReset = (field: LogoField) => {
    const config = LOGO_CONFIG[field];

    setSelectedFiles((prev) => ({
      ...prev,
      [field]: null,
    }));

    setFormData((prev) => ({
      ...prev,
      [config.pathField]: '',
      [config.urlField]: '',
    }));
  };

  const getLogoPreview = (field: LogoField) => {
    const config = LOGO_CONFIG[field];
    return logoPreviewUrls[field] || (resolvedBranding?.[config.resolvedKey] as string) || '';
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!clientId) {
      toast.error('Tenant não selecionado.');
      return;
    }
    if (settingsLoadError || loadedClientId !== clientId) {
      toast.error('Recarregue os dados da clínica antes de salvar. Nenhuma alteração foi enviada.');
      return;
    }

    const requestedClientId = clientId;

    const submittedTimeZone = formData.timeZone.trim();
    const isSupportedBrazilianTimeZone = BRAZIL_TIME_ZONE_OPTIONS.some(
      (option) => option.value === submittedTimeZone,
    );

    if (!isValidTimeZone(submittedTimeZone) || !isSupportedBrazilianTimeZone) {
      toast.error('Selecione um fuso horário brasileiro válido.');
      return;
    }

    setIsSaving(true);
    try {
      const payload: Partial<ClientReportSettings> = {
        ...formData,
        timeZone: submittedTimeZone,
      };

      for (const field of Object.keys(LOGO_CONFIG) as LogoField[]) {
        const selectedFile = selectedFiles[field];
        if (!selectedFile) {
          continue;
        }

        const config = LOGO_CONFIG[field];
        const uploadedAsset = await uploadClientBrandingImage(requestedClientId, config.kind, selectedFile);

        if (activeClientIdRef.current !== requestedClientId) {
          throw new Error('O tenant selecionado mudou durante o envio. Reabra a configuração antes de salvar.');
        }

        payload[config.pathField as keyof ClientReportSettings] = uploadedAsset.path as never;
        payload[config.urlField as keyof ClientReportSettings] = uploadedAsset.publicUrl as never;
      }

      if (activeClientIdRef.current !== requestedClientId) {
        throw new Error('O tenant selecionado mudou. Nenhuma configuração foi salva.');
      }
      await ClientReportSettingsService.upsertForClient(requestedClientId, payload);

      if (activeClientIdRef.current !== requestedClientId) {
        toast.success('Dados salvos para a clínica anterior. A seleção atual não foi recarregada.');
        return;
      }

      toast.success('Dados da clínica atualizados com sucesso.');
      await Promise.all([loadSettings(), refreshProfile()]);
    } catch (error: unknown) {
      console.error('Erro ao salvar dados da clínica:', error);
      toast.error(error instanceof Error ? error.message : 'Não foi possível salvar os dados da clínica.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedClient) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        Selecione um tenant para editar os dados da clínica.
      </div>
    );
  }

  const canManageClinicSettings =
    selectedClient.userRole === 'admin' || user?.userGlobalRole === 'superAdmin';

  if (!canManageClinicSettings) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        Somente administradores da clínica podem alterar os dados institucionais e o fuso horário.
      </div>
    );
  }

  if (!isLoading && (settingsLoadError || loadedClientId !== clientId)) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-900">
        <p>{settingsLoadError || 'Os dados carregados não pertencem ao tenant selecionado.'}</p>
        <button
          type="button"
          onClick={() => void loadSettings()}
          className="mt-4 inline-flex items-center gap-2 rounded-xl border border-red-300 bg-white px-4 py-2 text-sm font-semibold"
        >
          <RefreshCcw className="h-4 w-4" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-blue-100 p-3 text-blue-700">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Dados da Clínica</h2>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">
                Configure a identidade visual e os dados institucionais do tenant para relatórios, laudos e
                comunicações futuras. Os dados bancários continuam no módulo financeiro.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-800">Tenant:</span> {selectedClient.clientName}
            </p>
            <p>
              <span className="font-semibold text-slate-800">Origem atual:</span>{' '}
              {resolvedBranding?.source === 'db' ? 'Configuração do banco' : 'Fallback do sistema'}
            </p>
            <p>
              <span className="font-semibold text-slate-800">Bucket:</span> {CLIENT_BRANDING_BUCKET}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Identidade visual</h3>
                <p className="text-sm text-slate-500">Os arquivos são enviados para um bucket dedicado por tenant.</p>
              </div>
              <button
                type="button"
                onClick={loadSettings}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <RefreshCcw className="h-4 w-4" />
                Recarregar
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {(Object.keys(LOGO_CONFIG) as LogoField[]).map((field) => {
                const config = LOGO_CONFIG[field];
                const previewUrl = getLogoPreview(field);

                return (
                  <div key={field} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{config.label}</p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">{config.description}</p>
                      </div>
                      <ImagePlus className="mt-1 h-4 w-4 text-slate-400" />
                    </div>

                    <div className="mb-4 flex h-36 items-center justify-center overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-white">
                      {previewUrl ? (
                        <img src={previewUrl} alt={config.label} className="h-full w-full object-contain p-3" />
                      ) : (
                        <div className="px-4 text-center text-sm text-slate-400">Nenhuma imagem configurada</div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800">
                        <Upload className="h-4 w-4" />
                        Escolher arquivo
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(event) => handleFileSelection(field, event.target.files?.[0] ?? null)}
                        />
                      </label>

                      <p className="min-h-[20px] text-xs text-slate-500">
                        {selectedFiles[field] ? `Arquivo selecionado: ${selectedFiles[field]?.name}` : 'Nenhum novo arquivo pendente.'}
                      </p>

                      <button
                        type="button"
                        onClick={() => handleLogoReset(field)}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-white"
                      >
                        Limpar referência
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Dados institucionais</h3>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Nome de exibição</label>
                <input
                  type="text"
                  value={formData.reportDisplayName}
                  onChange={(event) => handleFieldChange('reportDisplayName', event.target.value)}
                  placeholder={resolvedBranding?.displayName || selectedClient.clientName}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Razão social</label>
                <input
                  type="text"
                  value={formData.legalName}
                  onChange={(event) => handleFieldChange('legalName', event.target.value)}
                  placeholder={resolvedBranding?.legalName || ''}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">CNPJ</label>
                <input
                  type="text"
                  value={formData.cnpj}
                  onChange={(event) => handleFieldChange('cnpj', event.target.value)}
                  placeholder={resolvedBranding?.cnpj || ''}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Subtítulo institucional</label>
                <input
                  type="text"
                  value={formData.reportSubtitle}
                  onChange={(event) => handleFieldChange('reportSubtitle', event.target.value)}
                  placeholder={resolvedBranding?.reportSubtitle || ''}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Texto legal do rodapé</label>
                <textarea
                  value={formData.footerLegalText}
                  onChange={(event) => handleFieldChange('footerLegalText', event.target.value)}
                  placeholder={resolvedBranding?.footerLegalLine || ''}
                  rows={3}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Contato e endereço</h3>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Telefone curto</label>
                <input
                  type="text"
                  value={formData.contactPhone}
                  onChange={(event) => handleFieldChange('contactPhone', event.target.value)}
                  placeholder="(43) 3398-9011"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Telefone completo</label>
                <input
                  type="text"
                  value={formData.contactPhoneFull}
                  onChange={(event) => handleFieldChange('contactPhoneFull', event.target.value)}
                  placeholder={resolvedBranding?.contactLine || ''}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">WhatsApp curto</label>
                <input
                  type="text"
                  value={formData.contactWhatsapp}
                  onChange={(event) => handleFieldChange('contactWhatsapp', event.target.value)}
                  placeholder="(43) 99999-9999"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">WhatsApp completo</label>
                <input
                  type="text"
                  value={formData.contactWhatsappFull}
                  onChange={(event) => handleFieldChange('contactWhatsappFull', event.target.value)}
                  placeholder="+55 43 99999-9999"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">E-mail institucional</label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(event) => handleFieldChange('contactEmail', event.target.value)}
                  placeholder="contato@clinica.com"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Rua / Avenida</label>
                <input
                  type="text"
                  value={formData.addressStreet}
                  onChange={(event) => handleFieldChange('addressStreet', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Número</label>
                <input
                  type="text"
                  value={formData.addressNumber}
                  onChange={(event) => handleFieldChange('addressNumber', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Complemento</label>
                <input
                  type="text"
                  value={formData.addressComplement}
                  onChange={(event) => handleFieldChange('addressComplement', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Bairro</label>
                <input
                  type="text"
                  value={formData.addressDistrict}
                  onChange={(event) => handleFieldChange('addressDistrict', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Cidade</label>
                <input
                  type="text"
                  value={formData.addressCity}
                  onChange={(event) => handleFieldChange('addressCity', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Estado</label>
                <input
                  type="text"
                  value={formData.addressState}
                  onChange={(event) => handleFieldChange('addressState', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Clock3 className="h-4 w-4" />
                  Fuso horário da clínica
                </label>
                <select
                  value={formData.timeZone}
                  onChange={(event) => handleFieldChange('timeZone', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {BRAZIL_TIME_ZONE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} — {option.value}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  A agenda usa este fuso, independentemente do computador do usuário.
                  A troca altera a interpretação e a exibição local; os instantes históricos não são regravados automaticamente.
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">CEP</label>
                <input
                  type="text"
                  value={formData.addressPostalCode}
                  onChange={(event) => handleFieldChange('addressPostalCode', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving || isLoading}
              className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Salvando...' : 'Salvar dados da clínica'}
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Prévia atual</h3>
            <p className="mt-1 text-sm text-slate-500">
              Resolução efetiva usada hoje pelos relatórios V2, combinando banco e fallback.
            </p>

            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center gap-4 border-b border-slate-200 pb-4">
                <div className="flex h-20 w-24 items-center justify-center overflow-hidden rounded-2xl bg-white p-3 shadow-sm">
                  {resolvedBranding?.logoPrintUrl ? (
                    <img
                      src={resolvedBranding.logoPrintUrl}
                      alt={resolvedBranding.logoAlt}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-slate-400">Sem logo</span>
                  )}
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-900">{resolvedBranding?.displayName || selectedClient.clientName}</p>
                  <p className="text-sm text-slate-500">{resolvedBranding?.reportSubtitle || 'Sem subtítulo configurado'}</p>
                </div>
              </div>

              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <p>
                  <span className="font-semibold text-slate-800">Rodapé legal:</span>{' '}
                  {resolvedBranding?.footerLegalLine || 'Não configurado'}
                </p>
                <p>
                  <span className="font-semibold text-slate-800">Endereço:</span>{' '}
                  {[resolvedBranding?.addressLine1, resolvedBranding?.addressLine2].filter(Boolean).join(' | ') || 'Não configurado'}
                </p>
                <p>
                  <span className="font-semibold text-slate-800">Contato:</span>{' '}
                  {resolvedBranding?.contactLine || 'Não configurado'}
                </p>
                <p>
                  <span className="font-semibold text-slate-800">Fuso horário:</span>{' '}
                  {getTimeZoneLabel(formData.timeZone)} ({normalizeTimeZone(formData.timeZone)})
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-blue-200 bg-blue-50 p-6 text-sm text-blue-900 shadow-sm">
            <p className="font-semibold">Escopo desta primeira versão</p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>Salva dados institucionais do tenant em `client_report_settings`.</li>
              <li>Faz upload das logos para bucket dedicado por tenant.</li>
              <li>Passa a alimentar os relatórios V2 sem depender apenas do fallback.</li>
              <li>Define o fuso operacional usado pela agenda do tenant.</li>
              <li>Não altera o módulo financeiro nem substitui os dados bancários existentes.</li>
            </ul>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
          Carregando dados da clínica...
        </div>
      )}
    </div>
  );
}
